/**
 * Message utilities for chat messages
 * 
 * Message structure: { role: "user"|"assistant", content: string, id?, timestamp?, metadata?, source?, ... }
 */

// Creates a message object
export function createMessage({
  role,
  content,
  id = null,
  timestamp = null,
  metadata = null,
  source = null,
  ...otherFields
}) {
  const message = {
    role: role === "assistant" ? "assistant" : "user",
    content: String(content || ""),
  };

  // if (role === "assistant") {
  //   message.content = trimWordStart(message.content, "ANSWER:");
  // }

  if (id) message.id = id;
  if (timestamp) message.timestamp = timestamp;
  else message.timestamp = new Date().toISOString();

  if (metadata && typeof metadata === "object") {
    message.metadata = metadata;
  }

  if (source) message.source = source;

  if (Object.keys(otherFields).length > 0) {
    Object.assign(message, otherFields);
  }

  return message;
}

// Validates a message object
export function isValidMessage(message) {
  if (!message || typeof message !== "object") return false;
  if (message.role !== "user" && message.role !== "assistant") return false;
  if (typeof message.content !== "string" || !message.content.trim()) return false;
  return true;
}

// Merges additional data into an existing message - checkpoint integration
export function mergeMessageData(message, additionalData) {
  if (!isValidMessage(message)) return message;
  if (!additionalData || typeof additionalData !== "object") return message;

  return {
    ...message,
    ...additionalData,
    role: message.role,
    content: message.content,
    metadata: {
      ...(message.metadata || {}),
      ...(additionalData.metadata || {}),
    },
  };
}

export function trimWordFromStart(text, wordToTrim) {
  const regex = new RegExp(`^${wordToTrim}`); 
  const trimmedText = text.replace(regex, '').trim();
  return trimmedText;
}

// Converts API message to internal format
export function fromApiMessage(apiMessage, source = null) {
  if (!apiMessage || typeof apiMessage !== "object") return null;
  if (apiMessage.role !== "user" && apiMessage.role !== "assistant") return null;
  if (!apiMessage.content) return null;

  let content = apiMessage.content;
  if (apiMessage.role === "assistant") {
    content = trimWordFromStart(apiMessage.content, "ANSWER:");
  }

  return createMessage({
    role: apiMessage.role,
    content,
    id: apiMessage.id || apiMessage.message_id || null,
    timestamp: apiMessage.timestamp || apiMessage.created_at || null,
    metadata: apiMessage.metadata || {},
    source: source || apiMessage.source || null,
    ...Object.fromEntries(
      Object.entries(apiMessage).filter(
        ([key]) => !["role", "content", "id", "message_id", "timestamp", "created_at", "metadata", "source"].includes(key)
      )
    ),
  });
}

// Filters messages with validation
export function filterMessages(messages, filterFn) {
  if (!Array.isArray(messages)) return [];
  return messages.filter((msg) => isValidMessage(msg) && filterFn(msg));
}

// Checks if text starts with a fully capitalized word (for filtering debug/system messages)
export function startsWithFullyCapitalizedWord(text) {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;

  const firstWordMatch = trimmed.match(/^([A-Za-z]+)/);
  if (!firstWordMatch) return false;

  const firstWord = firstWordMatch[1];
  // Only consider multi-letter words (avoid filtering "A valid message", "I think...")
  if (firstWord.length < 2) return false;
  // Allow "ANSWER" / "ANSWER:" to pass (don't filter)
  if (firstWord === "ANSWER") return false;
  return firstWord === firstWord.toUpperCase() && /[A-Z]/.test(firstWord);
}
